import type { ReactNode } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { Box, Button, Typography } from "@mui/material";

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
  readonly onReset?: () => void;
}

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function DefaultFallback({ error, resetErrorBoundary }: FallbackProps): ReactNode {
  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 600,
        mx: "auto",
        mt: 4
      }}
    >
      <Typography variant="h6" color="error" gutterBottom>
        Something went wrong
      </Typography>
      <Typography
        component="pre"
        variant="body2"
        sx={{
          overflow: "auto",
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          mb: 2,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        }}
      >
        {error.message}
      </Typography>
      <Button
        variant="contained"
        onClick={() => {
          resetErrorBoundary();
        }}
      >
        Try again
      </Button>
    </Box>
  );
}

export function ErrorBoundary({ children, fallback, onReset }: ErrorBoundaryProps): ReactNode {
  const handleReset = (): void => {
    onReset?.();
  };

  if (fallback !== undefined) {
    return (
      <ReactErrorBoundary fallback={fallback} onReset={handleReset}>
        {children}
      </ReactErrorBoundary>
    );
  }

  return (
    <ReactErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <DefaultFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetErrorBoundary={() => {
            resetErrorBoundary();
            handleReset();
          }}
        />
      )}
      onReset={handleReset}
    >
      {children}
    </ReactErrorBoundary>
  );
}
